<script setup lang="ts">
import { useDynamicHeight } from '@/composables/useDynamicHeight';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { type Shift, type ShiftWithoutFinancialInformation } from '@/api/shifts';
import { clinicalAuthorName, SYSTEM_AI_AUTHOR } from '@/utils/clinical-author';
import { useShowVoided } from '@/composables/useShowVoided';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';

const props = defineProps<{ shift: Shift | ShiftWithoutFinancialInformation }>();

// The shift's data comes from its parent, which reloads it after an anulación.
const emit = defineEmits<{ voided: [] }>();

const { showVoided, voidedCount, visibleItems } = useShowVoided(() => props.shift?.reportImages);

const { dynamicHeight } = useDynamicHeight();
const { tableProps } = useResponsiveTable();

const headers = [
  { title: 'Fecha', value: 'humanCreatedAt' },
  { title: 'Registrado por', value: 'author' },
  { title: 'Imagen', value: 'imageUrlLarge' },
  { title: 'Texto', value: 'formattedText' },
];
</script>
<template>
  <v-data-table
    v-bind="tableProps"
    :headers="headers"
    :header-props="{ class: 'font-weight-bold' }"
    :items="visibleItems"
    :height="dynamicHeight"
    fixed-header
    item-key="id"
    class="pa-2"
    no-data-text="No hay registros subidos"
  >
    <template
      v-if="voidedCount > 0"
      #top
    >
      <div class="justify-end d-flex pa-2">
        <show-voided-switch
          v-model="showVoided"
          :count="voidedCount"
        />
      </div>
    </template>
    <template #[`item.humanCreatedAt`]="{ item }">
      <span class="text-no-wrap">{{ item.humanCreatedAt }}</span>
      <clinical-record-void
        item-type="ReportImage"
        :item-id="item.id"
        :can-void="item.canVoid"
        :label="`Registro visual · ${item.humanCreatedAt}`"
        @voided="emit('voided')"
      />
      <clinical-void-mark
        :voided="item.voided"
        :details="item.voidDetails"
      />
    </template>
    <template #[`item.author`]="{ item }">
      {{ clinicalAuthorName(item.author) || '-' }}
    </template>
    <template #[`item.imageUrlLarge`]="{ item }">
      <v-dialog max-width="600">
        <template #activator="{ props: activatorProps }">
          <v-img
            v-bind="activatorProps"
            :src="item.imageUrlLarge"
            width="100"
            height="100"
            class="my-2"
            :class="{ 'voided-image': item.voided }"
            contain
          />
        </template>
        <template #default>
          <v-img :src="item.imageUrlLarge" />
        </template>
      </v-dialog>
    </template>
    <template #[`item.formattedText`]="{ item }">
      <v-dialog max-width="600">
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-if="item.formattedText?.length > 0"
            v-bind="activatorProps"
            icon="mdi-eye"
            size="small"
            class="me-2"
          />
        </template>
        <template #default>
          <v-card>
            <v-card-text>
              <clinical-author-line
                :author="SYSTEM_AI_AUTHOR"
                label="Texto leído por"
                class="mb-2"
              />
              <div class="pre-wrap">
                {{ item.formattedText }}
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-dialog>
    </template>
  </v-data-table>
</template>
<style scoped>
/* An anulado photo stays visible, dimmed; its "Anulado" mark says why. */
.voided-image {
  opacity: .5;
}

.pre-wrap {
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>
